import logger from "./logger";

/**
 * Safely access a localStorage value
 */
export const getStorageItem = <V = string>(
  key: string,
  fallbackValue: V
): V => {
  try {
    return (localStorage.getItem(key) as V) ?? fallbackValue;
  } catch (err) {
    logger.log(err);
    return fallbackValue;
  }
};

/**
 * Safely remove a localStorage item
 */
export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    logger.log(err);
  }
};

/**
 * Safely set a localStorage value
 */
export const setStorageItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    logger.log(err);
    return false;
  }
};

/**
 * Safely set a localStorage value as JSON
 */
export const setStorageItemJson = (key: string, value: unknown): boolean => {
  try {
    const jsonValue = JSON.stringify(value);
    localStorage.setItem(key, jsonValue);
    return true;
  } catch (err) {
    logger.log(err);
    return false;
  }
};

/**
 * Safely get a localStorage value as JSON
 */
export const getStorageItemJson = <T>(key: string, fallbackValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return fallbackValue;
    }
    return JSON.parse(item) as T;
  } catch (err) {
    logger.log(err);
    return fallbackValue;
  }
};

/**
 * Safely access a sessionStorage value
 */
export const getSessionItem = <V = string>(
  key: string,
  fallbackValue: V
): V => {
  try {
    return (sessionStorage.getItem(key) as V) ?? fallbackValue;
  } catch (err) {
    logger.log(err);
    return fallbackValue;
  }
};

/**
 * Reads the JWT token and returns its value.
 * If not defined, or has expired then returns false.
 *
 * Note: this helper should be used instead of arenko-ui values:
 * `jwt_token_expiration` and `jwt_token`
 */
export const getJwtToken = (name = "jwt_token"): string | false => {
  const expName = `${name}_expiration`;
  let token = "";
  let expiration = 0;

  try {
    token = localStorage.getItem(name) ?? "";
    expiration = Number(localStorage.getItem(expName) ?? 0);
  } catch (err) {
    logger.log("Cannot retrieve JWT token", err);
    return false;
  }

  if (!token || expiration <= Date.now()) {
    return false;
  }

  return token;
};

/**
 *
 * @param name name of the storage value
 * @param token the JWT token string
 * @param expiry the lifetime of the token in seconds
 */
export const setJwtToken = (
  name: string,
  token: string,
  expirationSecs: number
) => {
  const expName = `${name}_expiration`;
  const expiration = expirationSecs * 1000;

  const result =
    setStorageItem(name, token) &&
    setStorageItem(expName, String(Date.now() + expiration));

  return result;
};

/**
 * Removes the JWT token from storage
 */
export const deleteJwtToken = (name = "jwt_token") => {
  const expName = `${name}_expiration`;
  try {
    localStorage.removeItem(name);
    localStorage.removeItem(expName);
  } catch (err) {
    logger.log(`Cannot delete JWT token ${name}`, err);
  }
};
